
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { StudentFormData, convertFormDataToStudent } from '../hooks/student-form/types';
import { Student, QualificationLevel, FeeStatus } from '@/services/db/types';
import { trackStudentRecordModification } from '@/services/db/admin/adminActionTracking';
import { unifiedStudentStorage } from './storage/unifiedStudentStorage';

export async function saveStudent(studentData: StudentFormData) {
  try {
    console.log('🚀 Starting unified student save:', studentData.name);
    
    // Enhanced validation
    if (!studentData.name || studentData.name.trim().length === 0) {
      throw new Error('Student name is required and cannot be empty');
    }
    
    if (!studentData.programs || studentData.programs.length === 0) {
      throw new Error('At least one program is required');
    }
    
    // Validate each program
    for (let i = 0; i < studentData.programs.length; i++) {
      const program = studentData.programs[i];
      if (!program.title || program.title.trim().length === 0) {
        throw new Error(`Program ${i + 1}: Title is required`);
      }
      if (!program.category || program.category.trim().length === 0) {
        throw new Error(`Program ${i + 1}: Category is required`);
      }
    }
    
    console.log('✅ Validation passed, converting form data...');
    const studentToSave = convertFormDataToStudent(studentData);
    
    const finalStudent = {
      ...studentToSave,
      name: studentData.name.trim(),
      age: studentData.age ? Number(studentData.age) : undefined,
      qualificationLevel: studentData.qualificationLevel as QualificationLevel || undefined,
      programs: (studentToSave.programs || []).map(program => ({
        ...program,
        feeStatus: program.feeStatus as FeeStatus,
        title: program.title.trim(),
        category: program.category.trim()
      }))
    };
    
    console.log('📦 Using unified storage for save operation...');
    
    let result;
    if (studentData.id) {
      // Update existing student
      console.log('✏️ Updating existing student...');
      result = await unifiedStudentStorage.updateStudent(Number(studentData.id), finalStudent);
      
      if (result) {
        await trackStudentRecordModification(
          result.id,
          'system',
          result,
          'Student record updated via unified storage'
        );
        console.log('✅ Student updated successfully with ID:', result.id);
        return result.id;
      } else {
        throw new Error('Failed to update student - student not found');
      }
    } else {
      // Add new student
      console.log('➕ Adding new student...');
      result = await unifiedStudentStorage.addStudent(finalStudent);
      
      await trackStudentRecordModification(
        result.id,
        'system',
        result,
        'New student record created via unified storage'
      );
      
      console.log('✅ Student created successfully with ID:', result.id);
      return result.id;
    }
    
  } catch (error) {
    console.error('❌ Error in unified student save:', error);
    
    if (error instanceof Error) {
      if (error.message.includes('quota') || error.message.includes('storage')) {
        throw new Error('Storage quota handled automatically. System supports 5000+ students with compression.');
      } else if (error.message.includes('validation') || error.message.includes('required')) {
        throw error;
      } else {
        throw new Error(`Failed to save student: ${error.message}`);
      }
    } else {
      throw new Error('An unexpected error occurred while saving student data');
    }
  }
}

export async function getStudentById(id: string) {
  try {
    console.log(`🔍 Getting student by ID via unified storage: ${id}`);
    
    const studentId = parseInt(id, 10);
    if (isNaN(studentId)) {
      console.error(`❌ Invalid student ID: ${id}`);
      return undefined;
    }
    
    const student = await unifiedStudentStorage.getStudentById(studentId);
    
    if (student) {
      console.log(`✅ Found student via unified storage: ${student.name} (ID: ${studentId})`);
      return student;
    }
    
    console.log(`❌ Student not found with ID: ${studentId}`);
    return undefined;
  } catch (error) {
    console.error('❌ Error fetching student via unified storage:', error);
    toast.error('Failed to load student data');
    throw error;
  }
}

export async function getAllStudents() {
  try {
    console.log('📚 Getting all students via unified storage...');
    const students = await unifiedStudentStorage.getAllStudents();
    console.log(`✅ Loaded ${students.length} students via unified storage`);
    return students;
  } catch (error) {
    console.error('❌ Error fetching all students:', error);
    toast.error('Failed to load students');
    throw error;
  }
}

export async function deleteStudent(id: number | string) {
  try {
    const studentId = typeof id === 'string' ? parseInt(id, 10) : id;
    
    console.log(`🗑️ Deleting student via unified storage: ${studentId}`);
    
    await trackStudentRecordModification(
      studentId,
      'system',
      { id: studentId },
      'Student record deleted via unified storage'
    );
    
    await unifiedStudentStorage.deleteStudent(studentId);
    console.log('✅ Student deleted successfully via unified storage');
    toast.success('Student deleted successfully');
  } catch (error) {
    console.error('❌ Error deleting student:', error);
    toast.error('Failed to delete student');
    throw error;
  }
}

export async function searchStudents(query: string) {
  try {
    console.log(`🔍 Searching students via unified storage: "${query}"`);
    const results = await unifiedStudentStorage.searchStudents(query);
    console.log(`✅ Found ${results.length} students matching "${query}"`);
    return results;
  } catch (error) {
    console.error('❌ Error searching students:', error);
    return [];
  }
}
